import { SendOutlined } from "@ant-design/icons"
import { App, Button } from "antd"
import dayjs from "dayjs"

import Flex from "#blocks/Flex"
import { useApiContext } from "#contexts/ApiContextProvider"
import { useAuthorizationContext } from "#contexts/AuthorizationContextProvider"
import { useUserContext } from "#contexts/UserContextProvider"
import { STANDARD_DATE_TIME_FORMAT } from "#utils/dateHelpers"
import { ErrorType, formatError, formatErrorArray } from "#utils/textFormatters"

type OpenErrorModal = (
  error: ErrorType | ErrorType[] | null | undefined,
  context: string
) => void

// Accepts a string to describe the action that caused the error (used in title)
export const useErrorModal = (action: string): OpenErrorModal => {
  const { modal, message } = App.useApp()
  const { apiHandler } = useApiContext()
  const { sendErrorToDevs } = apiHandler
  const { isManagerLoggedIn } = useAuthorizationContext()
  const { currentUser, currentUserId } = useUserContext()
  const currentUserName = currentUser?.name

  const openErrorModal = (
    error: ErrorType | ErrorType[] | null | undefined,
    context: string
  ) => {
    if (
      !error ||
      (Array.isArray(error) && error.length === 0) ||
      !isManagerLoggedIn
    ) {
      return
    }
    const content = Array.isArray(error)
      ? formatErrorArray(error)
      : formatError(error)

    // Enhance context with user information
    const enhancedContext = `${context}

    User: ${currentUserName || "Unknown"} (${currentUserId || "Unknown"})
    ${dayjs().format(STANDARD_DATE_TIME_FORMAT)}`

    console.log("❌ ERROR: ", error)
    const modalInstance = modal.error({
      title: `Error while ${action}`,
      content: (
        <Flex
          style={{
            whiteSpace: "pre-wrap",
            margin: "12px 0 24px",
          }}
        >
          {content}
        </Flex>
      ),
      footer: [
        <Flex style={{ justifyContent: "flex-end" }} gap={12}>
          <Button
            key="sendToDevs"
            onClick={async () => {
              await sendErrorToDevs(action, content, enhancedContext)
              message.success("Error sent! Thanks for your help!")
              modalInstance.destroy()
            }}
          >
            <SendOutlined />
            Send to devs
          </Button>
          <Button
            key="ok"
            type="primary"
            onClick={() => modalInstance.destroy()}
          >
            Ok
          </Button>
        </Flex>,
      ],
      maskClosable: true, // Allow clicking outside to close
    })
  }

  return openErrorModal
}

export default useErrorModal
